import React, { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Page } from '/imports/ui/Page';
import { PageBody } from '/imports/ui/PageBody';
import { PageHeading } from '/imports/ui/PageHeading';
import { Panel } from '/imports/ui/Panel';
import { Stat, StatRow } from '/imports/ui/Stat';
import { ChipGroup } from '/imports/ui/ChipGroup';
import { Badge } from '/imports/ui/Badge';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { useMethod, useUser } from '/imports/hooks';
import { getFunnelStats, getMarginStats, getInventoryStats, getChainCreditStats, getUnderwaterStock, FunnelCounts } from '/imports/api/analytics';
import { useChartTheme } from '/imports/ui/chartTheme';

const RANGES = [
  { id: '90d', label: '90d', days: 90 },
  { id: '12m', label: '12m', days: 365 },
  { id: 'all', label: 'All', days: null as number | null },
];

const int = (n: number) => n.toLocaleString();
const tix = (n: number) => `${(Math.round(n * 100) / 100).toLocaleString()} tix`;
/** Bare number for dense tables, where the unit belongs in the header. */
const num = (n: number | null | undefined) => (typeof n === 'number' ? (Math.round(n * 100) / 100).toLocaleString() : '--');
const rate = (a: number, b: number) => (b > 0 ? `${Math.round((a / b) * 100)}%` : '--');
const day = (d?: Date | null) => (d ? new Date(d).toISOString().slice(0, 10) : '--');

const FunnelBar = ({ counts, label }: { counts: FunnelCounts; label: string }) => {
  const total = Math.max(counts.opened, 1);
  const seg = (n: number, cls: string, title: string) => (
    n > 0 ? <div className={cls} style={{ width: `${(n / total) * 100}%` }} title={`${title}: ${int(n)}`} /> : null
  );
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between">
        <span className="text-[13px] font-semibold text-fg-1">{label}</span>
        <span className="font-mono text-[13px] tabular-nums text-fg-3">{int(counts.opened)} opened</span>
      </div>
      <div className="flex h-2.5 w-full overflow-hidden rounded-sm bg-page-alt">
        {seg(counts.completed, 'bg-primary', 'Completed')}
        {seg(counts.abandoned, 'bg-sell', 'Abandoned')}
        {seg(counts.dangling, 'bg-warn', 'Never closed')}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-fg-3">
        <span><span className="mr-1 inline-block h-2 w-2 rounded-coin bg-primary" />Completed {int(counts.completed)} ({rate(counts.completed, counts.opened)})</span>
        <span><span className="mr-1 inline-block h-2 w-2 rounded-coin bg-sell" />Abandoned {int(counts.abandoned)}</span>
        <span><span className="mr-1 inline-block h-2 w-2 rounded-coin bg-warn" />Never closed {int(counts.dangling)}</span>
      </div>
    </div>
  );
};

export const PerformancePage = () => {
  const user = useUser();
  const [rangeId, setRangeId] = useState('all');
  const theme = useChartTheme(user?.settings?.theme);
  const range = RANGES.find((r) => r.id === rangeId) || RANGES[RANGES.length - 1];

  const arg = useMemo(() => {
    if (range.days == null) return {};
    const to = new Date();
    return { from: new Date(to.getTime() - range.days * 86400000), to };
  }, [range.days]);

  const [funnel, funnelLoading] = useMethod(getFunnelStats, arg, [rangeId]);
  const [margin, marginLoading] = useMethod(getMarginStats, arg, [rangeId]);
  const [inventory] = useMethod(getInventoryStats, { days: 90 }, []);
  const [credit] = useMethod(getChainCreditStats, undefined, []);
  const [deficit] = useMethod(getUnderwaterStock, { limit: 50 }, []);

  if (!user?.isSuperUser) return <NotPermittedPage />;
  const loading = funnelLoading || marginLoading;

  return (
    <Page>
      <PageHeading
        left={<h1>Performance</h1>}
        right={<ChipGroup ariaLabel="Date range" chips={RANGES.map((r) => ({ id: r.id, label: r.label }))} value={rangeId} onChange={(id) => setRangeId(id)} />}
      />
      <PageBody>
        {loading || !funnel || !margin ? <LoadingFlex /> : (
          <div className="flex flex-col gap-3 pb-6">
            <StatRow>
              <Stat
                label="Realised margin"
                value={margin.salesLines ? tix(margin.realized) : '--'}
                icon="attachMoney"
                trend={margin.salesLines ? (margin.realized >= 0 ? 'up' : 'down') : undefined}
                delta={margin.salesLines ? tix(margin.proceeds) + ' in' : undefined}
                hint={margin.salesLines ? `${int(margin.unitsSold)} units sold` : 'no card sales recorded'}
              />
              <Stat
                label="Inventory at cost"
                value={tix(margin.openCost)}
                icon="documentSearch"
                hint={`${int(margin.openUnits)} units, per the ledger`}
              />
              <Stat
                label="Customer completion"
                value={rate(funnel.customer.completed, funnel.customer.opened)}
                icon="coPresent"
                hint={`${int(funnel.customer.opened)} trades opened`}
              />
              <Stat
                label="Internal transfers"
                value={int(funnel.internal.opened)}
                icon="switchAccess"
                hint={funnel.knownBotUids ? `vs ${funnel.knownBotUids} known bots` : 'no bot uids known'}
              />
              <Stat
                label="Credit held"
                value={credit ? tix(credit.total) : '--'}
                icon="wallet"
                hint={credit ? (credit.chains.length ? `at ${int(credit.chains.length)} bot chains, as they last stated it` : 'no chain has stated a balance') : 'loading'}
              />
            </StatRow>

            {/*
              Every honesty caveat the numbers carry, stated on the page rather
              than buried in a doc comment. Each one silently distorts a figure
              above it, so a reader who does not see them draws a wrong
              conclusion from a number that looks authoritative.
            */}
            {(margin.unmatchedUnits > 0 || margin.skippedCashLines > 0 || !funnel.knownBotUids || !margin.salesLines) && (
              <Panel className="border-warn/40">
                <div className="flex flex-col gap-1.5 text-[13px] text-fg-2">
                  {!margin.salesLines && (
                    <div><Badge tone="amber">No sales</Badge> <span className="ml-1">The ledger holds no card sales, only acquisitions, so realised margin cannot be computed. It will populate once sell-side trades are recorded.</span></div>
                  )}
                  {margin.unmatchedUnits > 0 && (
                    <div><Badge tone="amber">Incomplete history</Badge> <span className="ml-1">{int(margin.unmatchedUnits)} units were sold with no acquisition lot to match. Cost of goods is understated, so margin reads high.</span></div>
                  )}
                  {margin.skippedCashLines > 0 && (
                    <div><Badge tone="amber">Cash excluded</Badge> <span className="ml-1">{int(margin.skippedCashLines)} lines are priced in USD and are left out; dollars cannot be added to tickets.</span></div>
                  )}
                  {!funnel.knownBotUids && (
                    <div><Badge tone="red">Unclassified</Badge> <span className="ml-1">No bot uids are known, so internal transfers cannot be separated from customer trades and the completion rate above is inflated.</span></div>
                  )}
                </div>
              </Panel>
            )}

            <div className="grid gap-3 lg:grid-cols-2">
              <Panel eyebrow="Funnel" title="Trade outcomes" actions={<span className="text-[12px] text-fg-3">through {day(funnel.lastActivityEver)}</span>}>
                <div className="flex flex-col gap-5">
                  <FunnelBar counts={funnel.customer} label="Customer trades" />
                  <FunnelBar counts={funnel.internal} label="Internal fleet transfers" />
                </div>
              </Panel>

              <Panel eyebrow="Repeat business" title="Most frequent partners">
                {funnel.topPartners.length === 0 ? (
                  <div className="py-10 text-center text-[13px] text-fg-3">No customer trades in range</div>
                ) : (
                  <div className="flex flex-col">
                    <div className="eyebrow grid grid-cols-[1fr_auto_auto] gap-3 border-b border-soft pb-1.5 text-fg-3">
                      <span>Partner</span><span className="text-right">Trades</span><span className="text-right">Done</span>
                    </div>
                    {funnel.topPartners.map((p) => (
                      <div key={p.partnerId} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 border-b border-soft py-1.5 text-[13px] last:border-0">
                        <span className="truncate text-fg-1">{p.partner}</span>
                        <span className="text-right font-mono tabular-nums text-fg-2">{int(p.trades)}</span>
                        <span className="text-right font-mono tabular-nums text-fg-3">{rate(p.completed, p.trades)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>
            </div>

            {/*
              Inventory comes from the snapshot roll-up, not from `assets`:
              `assets` is destroyed and rebuilt every collection sync, so it can
              only ever answer "now". Until the nightly cron has run at least
              twice there is nothing to plot, and saying so beats an empty axis.
            */}
            <Panel
              eyebrow="Stock"
              title="Inventory over time"
              actions={<span className="text-[12px] text-fg-3">{inventory?.latest ? `${int(inventory.latest.units)} units held` : 'awaiting snapshots'}</span>}
            >
              {!inventory || inventory.series.length < 2 ? (
                <div className="flex flex-col items-center gap-1 py-14 text-center">
                  <div className="text-[13px] font-semibold text-fg-1">Not enough snapshots yet</div>
                  <div className="text-[12px] text-fg-3">
                    {inventory?.snapshotCount
                      ? `${int(inventory.snapshotCount)} recorded so far; the roll-up runs nightly and needs two points to draw a line.`
                      : 'The nightly roll-up has not run yet. Inventory history starts from its first run.'}
                  </div>
                </div>
              ) : (
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={inventory.series} margin={{ top: 4, right: 8, bottom: 0, left: -14 }}>
                      <CartesianGrid stroke={theme.grid} vertical={false} />
                      <XAxis dataKey="bucket" tick={{ fill: theme.axis, fontSize: 11 }} tickLine={false} axisLine={false} minTickGap={24} />
                      <YAxis tick={{ fill: theme.axis, fontSize: 11 }} tickLine={false} axisLine={false} width={60} />
                      <Tooltip contentStyle={{ background: theme.panel, border: `1px solid ${theme.grid}`, borderRadius: 6, fontSize: 12, color: theme.text }} labelStyle={{ color: theme.axis, fontSize: 11 }} />
                      <Bar dataKey="valueAtBuy" name="Value at cost (tix)" fill={theme.primary} radius={[3, 3, 0, 0]} />
                      <Bar dataKey="valueAtSell" name="Value at ask (tix)" fill={theme.tertiary} radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Panel>

            <div className="grid gap-3 lg:grid-cols-3">
              {/*
                Realised margin per card. Only meaningful once cards have SOLD,
                so it states that rather than rendering an empty table -- the
                ledger is buy-side only today.
              */}
              <Panel eyebrow="Contribution" title="Cards by realised margin" className="lg:col-span-3">
                {margin.topCards.length === 0 ? (
                  <div className="flex flex-col items-center gap-1 py-10 text-center">
                    <div className="text-[13px] font-semibold text-fg-1">Nothing sold yet</div>
                    <div className="text-[12px] text-fg-3">Per-card margin appears once cards are sold, not merely bought.</div>
                  </div>
                ) : (
                  <div className="flex flex-col">
                    <div className="eyebrow grid grid-cols-[1.6fr_auto_auto] gap-3 border-b border-soft pb-1.5 text-fg-3">
                      <span>Card</span><span className="text-right">Sold</span><span className="text-right">Margin</span>
                    </div>
                    {margin.topCards.map((c) => (
                      <div key={c.card} className="grid grid-cols-[1.6fr_auto_auto] items-center gap-3 border-b border-soft py-1.5 text-[13px] last:border-0">
                        <span className="truncate text-fg-1">{c.card}</span>
                        <span className="text-right font-mono tabular-nums text-fg-2">{int(c.unitsSold)}</span>
                        <span className={`text-right font-mono font-semibold tabular-nums ${c.realized > 0 ? 'text-buy' : c.realized < 0 ? 'text-sell' : 'text-fg-3'}`}>
                          {tix(c.realized)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>

              <Panel
                eyebrow="Distribution"
                title="Inventory by bot"
                className="lg:col-span-3"
                actions={<span className="text-[12px] text-fg-3">latest snapshot</span>}
              >
                {!inventory || inventory.perBot.length === 0 ? (
                  <div className="flex flex-col items-center gap-1 py-10 text-center">
                    <div className="text-[13px] font-semibold text-fg-1">No snapshot yet</div>
                    <div className="text-[12px] text-fg-3">The nightly roll-up has not run.</div>
                  </div>
                ) : (
                  <div className="flex flex-col">
                    <div className="eyebrow grid grid-cols-[1.6fr_auto_auto] gap-3 border-b border-soft pb-1.5 text-fg-3">
                      <span>Bot</span><span className="text-right">Units</span><span className="text-right">At cost</span>
                    </div>
                    {inventory.perBot.map((b) => (
                      <div key={b.bot} className="grid grid-cols-[1.6fr_auto_auto] items-center gap-3 border-b border-soft py-1.5 text-[13px] last:border-0">
                        <span className="truncate text-fg-1">{b.bot}</span>
                        <span className="text-right font-mono tabular-nums text-fg-2">{int(b.units)}</span>
                        <span className="text-right font-mono tabular-nums text-fg-1">{tix(b.valueAtBuy)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>

              {/*
                Held stock whose cost sits above what the market would pay for
                it today. Spans the grid because the interesting part is the
                comparison ACROSS the price columns: where FIFO and the lifetime
                average disagree the card has mixed lots, and where the Goatbots
                spread is wide the bid is a soft number.
              */}
              <Panel
                eyebrow="Deficit"
                title="Cards underwater"
                className="lg:col-span-3 [&>.panel-body]:max-h-[28rem] [&>.panel-body]:overflow-y-auto"
                actions={<span className="text-[12px] text-fg-3">{deficit ? `${tix(deficit.deficit)} paper loss` : ''}</span>}
              >
                {!deficit ? (
                  <div className="flex flex-col items-center gap-1 py-10 text-center">
                    <div className="text-[13px] font-semibold text-fg-1">Loading</div>
                    <div className="text-[12px] text-fg-3">Replaying the ledger to price the lots we still hold.</div>
                  </div>
                ) : deficit.cards.length === 0 ? (
                  <div className="flex flex-col items-center gap-1 py-10 text-center">
                    <div className="text-[13px] font-semibold text-fg-1">Nothing underwater</div>
                    <div className="text-[12px] text-fg-3">
                      {deficit.pricedCards
                        ? `All ${int(deficit.pricedCards)} cards with a recorded cost are held at or below the Goatbots bid.`
                        : 'No held card carries a recorded cost, so nothing can be compared.'}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col">
                    <div className="mb-2 text-[12px] text-fg-3">
                      {int(deficit.underwaterCards)} cards, {int(deficit.units)} units, {tix(deficit.deficit)} below the Goatbots bid across the whole position, at FIFO cost.
                      {deficit.costlessCards > 0 && ` ${int(deficit.costlessCards)} held cards cost us nothing on the ledger and are excluded.`}
                      {deficit.unquotedCards > 0 && ` ${int(deficit.unquotedCards)} have no Goatbots bid to measure against.`}
                    </div>
                    <div className="eyebrow grid grid-cols-[minmax(0,2fr)_auto_repeat(5,minmax(3.5rem,auto))] gap-3 border-b border-soft pb-1.5 text-fg-3">
                      <span>Card</span>
                      <span className="text-right">Held</span>
                      <span className="text-right">FIFO</span>
                      <span className="text-right">Avg</span>
                      <span className="text-right">GB bid</span>
                      <span className="text-right">GB ask</span>
                      <span className="text-right">Deficit</span>
                    </div>
                    {deficit.cards.map((u) => (
                      <div
                        key={u.cardId}
                        className="grid grid-cols-[minmax(0,2fr)_auto_repeat(5,minmax(3.5rem,auto))] items-center gap-3 border-b border-soft py-1.5 text-[13px] last:border-0"
                      >
                        <span className="truncate text-fg-1" title={u.ourAsk == null ? 'no ask set' : `we ask ${tix(u.ourAsk)}`}>{u.card}</span>
                        <span className="text-right font-mono tabular-nums text-fg-3">{int(u.units)}</span>
                        <span className="text-right font-mono tabular-nums text-fg-1">{num(u.unitCost)}</span>
                        {/* Bright only when it disagrees with FIFO, which is the mixed-lot tell. */}
                        <span className={`text-right font-mono tabular-nums ${u.avgCost != null && Math.abs(u.avgCost - u.unitCost) > 0.005 ? 'text-warn' : 'text-fg-3'}`}>{num(u.avgCost)}</span>
                        <span className="text-right font-mono tabular-nums text-fg-1">{num(u.marketBid)}</span>
                        <span className="text-right font-mono tabular-nums text-fg-3">{num(u.marketAsk)}</span>
                        <span className="text-right font-mono font-semibold tabular-nums text-sell">{num(u.deficit)}</span>
                      </div>
                    ))}
                    <div className="pt-2 text-[12px] text-fg-3">Prices in tickets. Deficit is FIFO cost minus the Goatbots bid for ONE copy, so the column does not sum to the total above. A highlighted average means the card has mixed lots, so FIFO and the average disagree.</div>
                  </div>
                )}
              </Panel>

              {/*
                Our store credit at external chains, exactly as each chain last
                stated it in chat: a chain figure is the sum over our bots, each
                bot's balance being the chain's latest statement to it. Never
                computed by us, so a chain we have not visited lately may read
                stale, and a chain that has never spoken has no row.
              */}
              <Panel
                eyebrow="Credit"
                title="Credit held by bot chains"
                actions={<span className="text-[12px] text-fg-3">{credit ? `${tix(credit.total)} total` : ''}</span>}
                className="[&>.panel-body]:max-h-[28rem] [&>.panel-body]:overflow-y-auto"
              >
                {!credit || credit.chains.length === 0 ? (
                  <div className="flex flex-col items-center gap-1 py-10 text-center">
                    <div className="text-[13px] font-semibold text-fg-1">{credit ? 'No credit stated yet' : 'Loading'}</div>
                    <div className="text-[12px] text-fg-3">Chains state our balance in chat on each trade; it is mirrored from there.</div>
                  </div>
                ) : (
                  <div className="flex flex-col">
                    <div className="eyebrow grid grid-cols-[1.6fr_auto_auto] gap-3 border-b border-soft pb-1.5 text-fg-3">
                      <span>Chain</span><span className="text-right">Bots</span><span className="text-right">Credit</span>
                    </div>
                    {credit.chains.map((c) => (
                      <div key={c.family} className="grid grid-cols-[1.6fr_auto_auto] items-center gap-3 border-b border-soft py-1.5 text-[13px] last:border-0">
                        <span className="truncate text-fg-1" title={`last stated ${day(c.latestAt)}`}>{c.family}</span>
                        <span className="text-right font-mono tabular-nums text-fg-2">{int(c.bots)}</span>
                        <span className="text-right font-mono tabular-nums text-fg-1">{tix(c.credit)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>
            </div>

            <Panel eyebrow="Volume" title="Customer trades opened vs completed" actions={<span className="text-[12px] text-fg-3">by {funnel.bucket}</span>}>
              {funnel.series.length === 0 ? (
                <div className="py-14 text-center text-[13px] text-fg-3">No customer trades in range</div>
              ) : (
                <div className="h-72 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={funnel.series} margin={{ top: 4, right: 8, bottom: 0, left: -14 }}>
                      <CartesianGrid stroke={theme.grid} vertical={false} />
                      <XAxis dataKey="bucket" tick={{ fill: theme.axis, fontSize: 11 }} tickLine={false} axisLine={false} minTickGap={24} />
                      <YAxis tick={{ fill: theme.axis, fontSize: 11 }} tickLine={false} axisLine={false} width={54} />
                      <Tooltip
                        contentStyle={{ background: theme.panel, border: `1px solid ${theme.grid}`, borderRadius: 6, fontSize: 12, color: theme.text }}
                        labelStyle={{ color: theme.axis, fontSize: 11 }}
                      />
                      <Bar dataKey="opened" name="Opened" fill={theme.tertiary} radius={[3, 3, 0, 0]} />
                      <Bar dataKey="completed" name="Completed" fill={theme.primary} radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Panel>
          </div>
        )}
      </PageBody>
    </Page>
  );
};
